import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Recycle, Building2, Layers, HelpCircle } from "lucide-react";

/**
 * Filtro rápido por Dirección General (Residuos · Servicios Urbanos · Todas).
 *
 * Mapping según catálogo CPV cliente:
 *   - "Residuos"          → direccion_general === "TRATAMIENTO"
 *   - "Servicios Urbanos" → direccion_general === "SERVICIOS"
 *   - "Todas"             → sin filtro
 *
 * Iter134 → Iter140 · Regla revisada tras feedback del usuario:
 *   Cuando el pill DG está activo (TRATAMIENTO o SERVICIOS) el filtro es
 *   **estricto**: los documentos sin `direccion_general` NO pasan.
 *   Motivo: la regla 5a original hacía que datos sin clasificar (mayoría en
 *   producción sin enriquecer) mantuvieran todos los KPIs iguales al total.
 *
 * Persistencia:
 *   - localStorage: `valoriza-dg-filter-{email}` (por usuario, sincroniza tabs).
 *   - Backend: `PUT /api/auth/me/preferences` con {last_dg_filter}: se usa
 *     para filtrar los digests semanales del destinatario.
 */
export const DG_LABELS = {
  "":            "Todas",
  "TRATAMIENTO": "Residuos",
  "SERVICIOS":   "Servicios Urbanos",
  "SIN_DG":      "Sin DG",
};
export const DG_OPTIONS = ["", "TRATAMIENTO", "SERVICIOS", "SIN_DG"];

const LS_KEY_FOR = (email) => `valoriza-dg-filter-${email || "anon"}`;

/** Hook: `{dg, setDg, filterByDG(items)}` — filtra items respetando la regla
 *  "docs sin DG siempre visibles". */
export function useDGFilter(userEmail) {
  const key = LS_KEY_FOR(userEmail);
  const [dg, setDgState] = useState(() => {
    try { return localStorage.getItem(key) || ""; } catch { return ""; }
  });

  // Sincroniza si el user cambia
  useEffect(() => {
    try { setDgState(localStorage.getItem(key) || ""); } catch { /* no-op */ }
  }, [key]);

  const setDg = useCallback((value) => {
    const v = value || "";
    setDgState(v);
    try { localStorage.setItem(key, v); } catch { /* no-op */ }
    // best-effort sync a backend (para digests filtrados). Ignora errores.
    axios.put(`${API}/auth/me/preferences`, { last_dg_filter: v }).catch(() => {});
    // Notifica a otras vistas de la misma pestaña
    try {
      window.dispatchEvent(new CustomEvent("dg-filter-changed", { detail: { dg: v } }));
    } catch { /* no-op */ }
  }, [key]);

  // Reactivo entre tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === key) setDgState(e.newValue || "");
    };
    const onCustom = (e) => {
      if (e?.detail && typeof e.detail.dg === "string") setDgState(e.detail.dg);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("dg-filter-changed", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("dg-filter-changed", onCustom);
    };
  }, [key]);

  const filterByDG = useCallback((items) => {
    if (!dg) return items || [];
    return (items || []).filter(it => {
      const v = (it?.direccion_general || "").toString().trim();
      // Iter140 · filtro estricto: sin DG NO pasa cuando hay pill activo.
      // Iter179.6 · Chip "Sin DG": muestra sólo los que NO tienen DG asignada.
      if (dg === "SIN_DG") return !v;
      return v === dg;
    });
  }, [dg]);

  return { dg, setDg, filterByDG };
}

/** Componente visual del filtro (3 pills). Reutilizable en cualquier módulo. */
export default function DGFilter({ value, onChange, size = "md", className = "", testidPrefix = "dg-filter" }) {
  const cx = size === "sm" ? "text-[10px] px-2 py-1" : "text-xs px-2.5 py-1.5";
  const btn = (val, label, Icon, activeCls) => {
    const active = value === val;
    return (
      <button
        key={val || "all"}
        onClick={() => onChange(val)}
        data-testid={`${testidPrefix}-${val || "all"}`}
        aria-pressed={active}
        className={`${cx} rounded-full font-semibold border transition-all inline-flex items-center gap-1 ${
          active
            ? activeCls
            : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:text-slate-700"
        }`}>
        <Icon size={size === "sm" ? 10 : 12}/> {label}
      </button>
    );
  };

  return (
    <div className={`inline-flex items-center gap-1 ${className}`} data-testid={testidPrefix} role="group" aria-label="Filtrar por dirección general">
      {btn("", DG_LABELS[""], Layers, "bg-slate-900 text-white border-slate-900")}
      {btn("TRATAMIENTO", DG_LABELS["TRATAMIENTO"], Recycle, "bg-emerald-600 text-white border-emerald-600")}
      {btn("SERVICIOS", DG_LABELS["SERVICIOS"], Building2, "bg-blue-600 text-white border-blue-600")}
      {btn("SIN_DG", DG_LABELS["SIN_DG"], HelpCircle, "bg-amber-500 text-white border-amber-500")}
    </div>
  );
}
